import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import { useMutation } from '@tanstack/react-query';
import { bulkResendOrderNotifications } from 'config/requests/orders';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext, useNotify, useResourceContext, useUnselectAll } from 'react-admin';
import { Link } from 'react-router-dom';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { BaseDialog } from 'shared/mui/Dialogs';
import { getId } from 'utils';

const BULK_RESEND_NOTIFICATIONS_LIMIT = 100;

export const OrdersListBulkActions: React.FC = () => {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const { createPathWithQuery } = useCreatePathWithQuery();
  const { hasResourceAccess, isHaveRole } = usePermissions();

  const { selectedIds } = useListContext();
  const resource = useResourceContext();
  const unselectAll = useUnselectAll(resource);

  const [isOpenAdjustmentDialog, setIsOpenAdjustmentDialog] = useState(false);
  const [isOpenResendDialog, setIsOpenResendDialog] = useState(false);

  const ids = selectedIds?.map((order) => getId(order)) || [];

  const canAdjustOrders = hasResourceAccess(Resources.OrderAdjustments);
  const canResendNotifications = isHaveRole([Role.Admin, Role.Payment]);
  const isResendDisabled = ids.length > BULK_RESEND_NOTIFICATIONS_LIMIT;

  const to = createPathWithQuery(
    { resource: Resources.OrderAdjustments, type: 'list' },
    {
      filter: {
        id: ids,
      },
    },
  );

  const bulkResendMutation = useMutation({
    mutationFn: () => bulkResendOrderNotifications(ids),
    onSuccess: () => {
      notify(translate('app.orders.bulkResendNotifications.successMessage'), {
        autoHideDuration: 3000,
      });
      setIsOpenResendDialog(false);
      unselectAll();
    },
  });

  return (
    <>
      {canAdjustOrders && (
        <Button
          label={translate('app.orders.ordersAdjustment')}
          onClick={() => setIsOpenAdjustmentDialog(true)}
          startIcon={<EditIcon />}
        />
      )}
      {canResendNotifications && (
        <Button
          disabled={isResendDisabled}
          label={translate('app.orders.bulkResendNotifications.button')}
          onClick={() => setIsOpenResendDialog(true)}
          startIcon={<ReplayIcon />}
        />
      )}
      <BaseDialog
        message={translate('app.orders.confirmGoToBulkOrders', {
          selectedIds: selectedIds?.length,
        })}
        onClose={() => setIsOpenAdjustmentDialog(false)}
        open={isOpenAdjustmentDialog}
        rightActionsSlot={[
          <CancelButton
            key="cancel-button"
            label={translate('app.buttons.cancel')}
            onClick={() => setIsOpenAdjustmentDialog(false)}
            variant="outlined"
          />,
          <Button
            component={Link}
            key="ok-button"
            label={translate('app.buttons.confirm')}
            onClick={() => setIsOpenAdjustmentDialog(false)}
            target="_blank"
            to={to}
            variant="contained"
          />,
        ]}
        title={translate('app.orders.goToOrdersAdjustment')}
      />
      <BaseDialog
        message={translate('app.orders.bulkResendNotifications.confirmMessage')}
        onClose={() => setIsOpenResendDialog(false)}
        open={isOpenResendDialog}
        rightActionsSlot={[
          <CancelButton
            key="cancel-button"
            label={translate('app.buttons.cancel')}
            onClick={() => setIsOpenResendDialog(false)}
            variant="outlined"
          />,
          <Button
            disabled={bulkResendMutation.isPending}
            key="ok-button"
            label={translate('app.buttons.confirm')}
            onClick={() => bulkResendMutation.mutate()}
            variant="contained"
          />,
        ]}
        title={translate('app.orders.bulkResendNotifications.confirmTitle')}
      />
    </>
  );
};
